/**
 * Skeleton Post Card
 * Animated placeholder shown during async content generation with progress tracking
 */

interface SkeletonPostCardProps {
  progress?: number; // 0-100
  statusMessage?: string;
  showCancelButton?: boolean;
  onCancel?: () => void;
}

export default function SkeletonPostCard({
  progress = 0,
  statusMessage = 'Generating content...',
  showCancelButton = false,
  onCancel,
}: SkeletonPostCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-4 relative overflow-hidden">
      {/* Progress Bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full bg-black dark:bg-white transition-all duration-300 ease-out"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>

      {/* Status Badge */}
      <div className="flex items-center justify-between mb-3 mt-1">
        <span className="inline-flex items-center px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 border-2 border-yellow-600 dark:border-yellow-500 rounded text-xs font-bold text-yellow-800 dark:text-yellow-400">
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          GENERATING ({progress}%)
        </span>
        <div className="text-xs text-gray-500 dark:text-gray-400">{statusMessage}</div>
      </div>

      {/* Title Skeleton */}
      <div className="mb-3">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-3/4 animate-pulse"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
      </div>

      {/* Platform Badges Skeleton */}
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        <div className="h-7 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        <div className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
      </div>

      {/* Token Cost Skeleton */}
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-28 mb-3 animate-pulse"></div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {showCancelButton ? (
          <button
            onClick={onCancel}
            className="flex-1 px-3 py-2 border-2 border-red-600 dark:border-red-400 text-red-600 dark:text-red-400 rounded font-medium text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            Cancel
          </button>
        ) : (
          <div className="flex-1 h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        )}
      </div>
    </div>
  );
}
